import { ErrorMessage } from '@/common/components/ErrorMessage'
import { VariantProps } from 'class-variance-authority'
import { ChangeEvent, FocusEvent, KeyboardEvent, useEffect, useMemo, useRef, useState } from 'react'
import { cn } from '../../utils/cn'
import { textAreaVariants } from '../TextArea/styles'
import { SuggestionData } from '@/common/types/highlightTextArea.type'
import { replacePeriodToUnderscore } from '@/common/utils/transformers'
import TextWithMentions from './TextWithMentions'
import SuggestionsMenu from './SuggestionsMenu'

export interface MenuPosition {
  top: number;
  left: number;
  showAbove: boolean;
}

interface MentionData {
  show: boolean;
  query: string;
  position: number;
}

interface CursorCoordinates {
  x: number;
  y: number;
  lineHeight: number;
}

export interface HighlightTextAreaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement>, VariantProps<typeof textAreaVariants> {
  label?: string;
  disabled?: boolean;
  RightIcon?: JSX.Element;
  LeftIcon?: JSX.Element;
  placeholder?: string;
  errorMessage?: string;
  value?: string;
  defaultValue?: string;
  rows?: number;
  markAsRequired?: boolean;
  name?: string;
  suggestions?: SuggestionData[];
  onChange?: (e: { target: { value: string } }) => void;
}

const HighlightTextArea = ({
  label,
  disabled = false,
  placeholder,
  errorMessage,
  markAsRequired,
  name,
  defaultValue,
  value: initialValue,
  onChange,
  onBlur,
  suggestions = [],
}: HighlightTextAreaProps) => {
  const [value, setValue] = useState<string>(initialValue || defaultValue || '');
  const [showSuggestions, setShowSuggestions] = useState<boolean>(false);
  const [mentionQuery, setMentionQuery] = useState<string>('');
  const [cursorPosition, setCursorPosition] = useState<number>(0);
  const [selectedIndex, setSelectedIndex] = useState<number>(0);
  const [menuPosition, setMenuPosition] = useState<MenuPosition>({ top: 0, left: 0, showAbove: false });

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const filteredSuggestions: SuggestionData[] = useMemo(() => {
    return mentionQuery.length
      ? suggestions.filter(suggestion =>
        suggestion.value.toLowerCase().includes(mentionQuery.toLowerCase())
      )
      : suggestions;
  }, [mentionQuery, suggestions.length])

  const calculateCursorPosition = (textarea: HTMLTextAreaElement, cursorPos: number): CursorCoordinates => {
    const textBeforeCursor = textarea.value.substring(0, cursorPos);

    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');

    if (!context)
      return { x: 0, y: 0, lineHeight: 20 }

    const computedStyle = window.getComputedStyle(textarea);
    context.font = `${computedStyle.fontSize} ${computedStyle.fontFamily}`;

    const lineHeight = parseInt(computedStyle.lineHeight) || parseInt(computedStyle.fontSize) * 1.2;
    const paddingTop = parseInt(computedStyle.paddingTop) || 0;
    const paddingLeft = parseInt(computedStyle.paddingLeft) || 0;
    const paddingRight = parseInt(computedStyle.paddingRight) || 0;

    const availableWidth = textarea.clientWidth - paddingLeft - paddingRight;

    const wrapText = (text: string, maxWidth: number): string[] => {
      const lines: string[] = [];
      const paragraphs = text.split('\n');

      for (const paragraph of paragraphs) {
        if (paragraph === '') {
          lines.push('');
          continue;
        }

        const words = paragraph.split(' ');
        let currentLine = '';

        for (const word of words) {
          const testLine = currentLine ? `${currentLine} ${word}` : word;
          const testWidth = context.measureText(testLine).width;

          if (testWidth <= maxWidth) {
            currentLine = testLine;
          } else {
            if (currentLine) {
              lines.push(currentLine);
              currentLine = word;
            } else {
              let remainingWord = word;

              while (remainingWord) {
                let charCount = 0;
                let testWord = '';

                for (let i = 0; i < remainingWord.length; i++) {
                  const testChar = remainingWord[i];
                  const newTestWord = testWord + testChar;

                  if (context.measureText(newTestWord).width <= maxWidth) {
                    testWord = newTestWord;
                    charCount++;
                  } else {
                    break;
                  }
                }

                if (charCount === 0) charCount = 1;

                lines.push(remainingWord.substring(0, charCount));
                remainingWord = remainingWord.substring(charCount);
              }
              currentLine = '';
            }
          }
        }

        if (currentLine) {
          lines.push(currentLine);
        }
      }

      return lines;
    };

    const wrappedLines = wrapText(textBeforeCursor, availableWidth);

    let currentLineIndex = wrappedLines.length - 1;
    const currentLineText = wrappedLines[currentLineIndex] || '';

    const textWidth = context.measureText(currentLineText).width;

    return {
      x: paddingLeft + textWidth,
      y: paddingTop + (currentLineIndex * lineHeight) + lineHeight,
      lineHeight,
    }
  }

  const calculateMenuPosition = (textarea: HTMLTextAreaElement, cursorPos: number): MenuPosition => {
    if (!textarea)
      return { top: 0, left: 0, showAbove: false };

    const textareaRect = textarea.getBoundingClientRect();
    const cursorCoords = calculateCursorPosition(textarea, cursorPos);

    const maxMenuHeight = 256;
    const menuWidth = 384;

    const itemHeight = 60;
    const actualMenuHeight = Math.min(filteredSuggestions.length * itemHeight, maxMenuHeight);

    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;

    const absoluteCursorTop = textareaRect.top + cursorCoords.y;
    const absoluteCursorLeft = textareaRect.left + cursorCoords.x;

    const spaceBelow = viewportHeight - absoluteCursorTop;
    const spaceAbove = absoluteCursorTop;

    const showAbove = spaceBelow < actualMenuHeight && spaceAbove > actualMenuHeight;

    let finalTop = absoluteCursorTop;

    if (showAbove)
      finalTop = absoluteCursorTop - actualMenuHeight - cursorCoords.lineHeight;

    let finalLeft = absoluteCursorLeft;

    if (finalLeft + menuWidth > viewportWidth)
      finalLeft = viewportWidth - menuWidth - 10;

    if (finalLeft < 10)
      finalLeft = 10;

    if (finalTop < 10)
      finalTop = 10;

    if (finalTop + actualMenuHeight > viewportHeight - 10)
      finalTop = viewportHeight - actualMenuHeight - 10;

    return {
      top: finalTop,
      left: finalLeft,
      showAbove,
    }
  }

  const detectMention = (text: string, position: number): MentionData => {
    const textBeforeCursor = text.substring(0, position);
    const lastAtIndex = textBeforeCursor.lastIndexOf('@');

    if (lastAtIndex !== -1) {
      const textAfterAt = textBeforeCursor.substring(lastAtIndex + 1);

      if (!textAfterAt.includes(' ') && !textAfterAt.includes('\n')) {
        return {
          show: true,
          query: textAfterAt,
          position: lastAtIndex,
        }
      }
    }

    return { show: false, query: '', position: -1 };
  };

  const scrollToSelectedItem = (): void => {
    if (menuRef.current && selectedIndex >= 0) {
      const selectedElement = menuRef.current.children[selectedIndex] as HTMLElement;

      if (selectedElement) {
        selectedElement.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest'
        });
      }
    }
  }

  const updateMenuPosition = () => {
    if (showSuggestions && textareaRef.current) {
      const menuPos = calculateMenuPosition(textareaRef.current, cursorPosition);

      const viewportHeight = window.innerHeight;
      const viewportWidth = window.innerWidth;

      const textareaRect = textareaRef.current.getBoundingClientRect();

      const isOutsideViewport =
        textareaRect.bottom < 0 ||
        textareaRect.top > viewportHeight ||
        textareaRect.right < 0 ||
        textareaRect.left > viewportWidth;

      if (isOutsideViewport) {
        setShowSuggestions(false);
      } else {
        setMenuPosition(menuPos);
      }
    }
  }

  useEffect(() => {
    if (showSuggestions)
      scrollToSelectedItem();
  }, [selectedIndex, showSuggestions])

  useEffect(() => {
    if (showSuggestions) {
      const handleScroll = () => {
        updateMenuPosition();
      }

      const handleResize = () => {
        updateMenuPosition();
      }

      window.addEventListener('scroll', handleScroll, true);
      window.addEventListener('resize', handleResize);

      return () => {
        window.removeEventListener('scroll', handleScroll, true);
        window.removeEventListener('resize', handleResize);
      };
    }
  }, [showSuggestions, cursorPosition]);

  useEffect(() => {
    if (showSuggestions && textareaRef.current) {
      const menuPos = calculateMenuPosition(textareaRef.current, cursorPosition);
      setMenuPosition(menuPos);
    }
  }, [showSuggestions, cursorPosition, mentionQuery]);

  const handleChange = (e: ChangeEvent<HTMLTextAreaElement>): void => {
    const newValue = e.target.value;
    const position = e.target.selectionStart;

    setValue(newValue);
    setCursorPosition(position);

    onChange?.({ target: { value: newValue } });

    const mention = detectMention(newValue, position);

    setShowSuggestions(mention.show);
    setMentionQuery(mention.query);
    setSelectedIndex(0);
  }

  const handleBlur = (e: FocusEvent<HTMLTextAreaElement, Element>) => {
    setTimeout(() => setShowSuggestions(false), 150);

    onBlur?.(e)
  }

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>): void => {
    if (showSuggestions && filteredSuggestions.length > 0) {
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setSelectedIndex(prev => prev < filteredSuggestions.length - 1 ? prev + 1 : 0);
          break;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex(prev => prev > 0 ? prev - 1 : filteredSuggestions.length - 1);
          break;
        case 'Enter':
          e.preventDefault();
          selectSuggestion(filteredSuggestions[selectedIndex]);
          break;
        case 'Escape':
          e.preventDefault();
          setShowSuggestions(false);
          break;
      }
    }
  }

  const selectSuggestion = (suggestion: SuggestionData): void => {
    const textBeforeCursor = value.substring(0, cursorPosition);
    const textAfterCursor = value.substring(cursorPosition);
    const lastAtIndex = textBeforeCursor.lastIndexOf('@');

    const newValue = value.substring(0, lastAtIndex) + `@${suggestion.value} ` + textAfterCursor;

    onChange?.({ target: { value: newValue } });

    setValue(newValue);
    setShowSuggestions(false);

    setTimeout(() => {
      if (textareaRef.current) {
        const newCursorPosition = lastAtIndex + suggestion.value.length + 2;

        textareaRef.current.focus();
        textareaRef.current.setSelectionRange(newCursorPosition, newCursorPosition);
      }
    }, 0);
  }

  const handleSuggestionClick = (suggestion: SuggestionData): void => {
    selectSuggestion(suggestion);
  }

  const resizeTextarea = () => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      const newHeight = textareaRef.current.scrollHeight;
      textareaRef.current.style.height = `${newHeight}px`;
    }
  };

  useEffect(() => { setTimeout(resizeTextarea) }, []);

  useEffect(() => resizeTextarea, [value]);

  return (
    <div className="relative w-full">
      <div className='flex flex-col'>
        {label && (
          <p className='text-xs mb-1'>
            {label}

            {markAsRequired && <span className="text-danger-soft"> *</span>}
          </p>
        )}

        <div className="relative">
          <textarea
            ref={textareaRef}
            value={value}
            onChange={handleChange}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            placeholder={!disabled ? (placeholder || 'Use @ para selecionar os tokens') : ''}
            className="w-full min-h-32 p-4 bg-white-200 rounded-md resize-none focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-transparent relative z-20 text-transparent"
            rows={4}
            style={{
              caretColor: '#ffffff',
              overflowY: 'auto',
            }}
            disabled={disabled}
            name={name}
            data-testid={name ? `${replacePeriodToUnderscore(name)}_textarea` : ''}
          />

          <div
            className={cn(
              'absolute top-0 left-0 w-full min-h-32 p-4 pointer-events-none whitespace-pre-wrap break-words z-10 text-white-800 overflow-y-auto',
              disabled && 'text-white-400'
            )}
            style={{
              font: 'inherit',
              lineHeight: 'inherit',
              letterSpacing: 'inherit',
              wordSpacing: 'inherit',
              overflowY: 'auto',
            }}
            data-testid={name ? `${replacePeriodToUnderscore(name)}_highlight_textarea_with_mentions` : ''}
          >
            <TextWithMentions
              value={value}
              suggestions={suggestions}
            />
          </div>
        </div>

        {errorMessage?.length && <ErrorMessage name={name} message={errorMessage} />}
      </div>

      <SuggestionsMenu
        showSuggestions={showSuggestions}
        filteredSuggestions={filteredSuggestions}
        menuPosition={menuPosition}
        selectedIndex={selectedIndex}
        handleSuggestionClick={handleSuggestionClick}
        menuRef={menuRef}
      />
    </div>
  )
}

export default HighlightTextArea
